import { Inject, Injectable, InjectionToken } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MJAuthBase } from '@memberjunction/ng-auth-services';
import { ApplicationManager, BaseApplication } from '@memberjunction/ng-base-application';
import { AGUScrollportHolder } from './scrollport';
import { AGUAppLocation, IsSameLocation, WithOneLevelFrom } from './app-location';
import { SyncSurfaceImmersiveClass } from './immersive-surface';

/**
 * Per-surface identity the adapter needs and cannot infer. Provide it on the
 * surface COMPONENT (with the adapter), never on an eagerly imported NgModule:
 * module providers land in the root injector, where two surfaces' configs
 * collide and the last import wins for both.
 */
export interface AGUAppSurfaceConfig {
    /** Element selector the immersive predicate measures (e.g. 'mj-agu-surface'). */
    readonly SurfaceSelector: string;
    /** Body class the global stylesheet hangs its chrome-hiding rules off. */
    readonly ImmersiveBodyClass: string;
    /**
     * MJ Application names this surface serves. Excluded from "return to host"
     * targets — sending a user back to the app they are already in changes the
     * URL and nothing else. A composite app and its single-module siblings all
     * render the same surface, so this is a list, not one name.
     */
    readonly OwnApplicationNames: readonly string[];
}

export const AGU_APP_SURFACE_CONFIG = new InjectionToken<AGUAppSurfaceConfig>('AGUAppSurfaceConfig');

/** Preferred "back to Explorer" destination when the user holds it. */
const EXPLORER_HOME_APP_NAME = 'Home';

/**
 * The single seam between a composed AGU app and the Explorer shell hosting it.
 *
 * Navigation is a BehaviorSubject rather than router state because a surface is
 * a routerless BaseDashboard. Data transport is deliberately NOT part of this
 * contract — modules talk to MJ through the provider the shell already
 * configured.
 *
 * Generalized from ExplorerFpHostAdapter, which is the correct of the two
 * pre-existing implementations: it resolves re-entry through
 * ApplicationManager.GetAppUrl and refuses to offer an exit the user cannot
 * actually take (see CanReturnToHost).
 */
@Injectable()
export class AGUAppSurfaceHostAdapter {
    private readonly location: BehaviorSubject<AGUAppLocation>;

    /**
     * Minimal back stack. Bounded because a long-lived Explorer tab accumulates
     * navigations for the life of the session, and only the most recent entries
     * are reachable through a single Back affordance anyway.
     */
    private readonly history: AGUAppLocation[] = [];
    private static readonly MaxHistoryDepth = 20;

    /** The surface element this adapter scrolls; see `AGUScrollportHolder`. */
    private readonly scrollport = new AGUScrollportHolder();

    constructor(
        @Inject(AGU_APP_SURFACE_CONFIG) protected readonly config: AGUAppSurfaceConfig,
        private readonly auth: MJAuthBase,
        private readonly router: Router,
        protected readonly apps: ApplicationManager,
        @Inject(DOCUMENT) private readonly document: Document
    ) {
        // A surface starts with no module resolved; the surface component sets the
        // real landing location once its composition and entitlements resolve.
        this.location = new BehaviorSubject<AGUAppLocation>({ ModuleKey: '', View: '' });

        // Belt-and-braces re-sync on every route change: tab activations are
        // exactly when the shell attaches/detaches surfaces, and a detached
        // surface (hidden tab) stays alive with its adapter — so this signal
        // still fires in windows where no component-level event does. The
        // setTimeout lets the shell finish moving DOM before we measure it.
        // Ends with the injector that owns the adapter: each surface provides
        // its adapter on its own component, so that is the surface's own
        // lifetime (its destroy re-syncs the class one last time itself).
        this.router.events.pipe(filter((e) => e instanceof NavigationEnd), takeUntilDestroyed()).subscribe(() => {
            setTimeout(() => this.SyncImmersiveClass());
        });
    }

    /** The surface registers its own element on init; modules then read/write `ScrollTop` through this adapter. */
    AttachScrollport(element: HTMLElement): void {
        this.scrollport.Attach(element);
    }

    DetachScrollport(element: HTMLElement): void {
        this.scrollport.Detach(element);
    }

    /** The surface's scroll offset; 0 and write-ignored until a scrollport is attached. */
    get ScrollTop(): number {
        return this.scrollport.ScrollTop;
    }

    set ScrollTop(value: number) {
        this.scrollport.ScrollTop = value;
    }

    /** Recompute immersive mode from on-screen truth. See immersive-surface.ts. */
    SyncImmersiveClass(): void {
        SyncSurfaceImmersiveClass(
            this.document,
            this.config.SurfaceSelector,
            this.config.ImmersiveBodyClass
        );
    }

    /** Emits the current location on subscribe and again on every change. */
    get Location$(): Observable<AGUAppLocation> {
        return this.location.asObservable();
    }

    get CurrentLocation(): AGUAppLocation {
        return this.location.value;
    }

    /**
     * True only when the user actually holds another application.
     *
     * Being a full MJ principal is not the same as having somewhere to go. Users
     * created by a bulk import often hold exactly one app grant, and for them the
     * surface IS their whole Explorer. Offering "Back to Explorer" stranded them:
     * the target URL matched the shell's `app/:appName` route but resolved to an
     * application they could not open, so no workspace tab changed, the surface
     * stayed on screen, and the immersive predicate — correctly measuring a
     * surface that was still visible — kept the shell header hidden. The click
     * changed the URL and nothing else, with no way back.
     */
    get CanReturnToHost(): boolean {
        return this.OtherApplicationTarget !== null;
    }

    ReturnToHost(): void {
        const target = this.OtherApplicationTarget;
        if (!target) {
            // Nothing to return to. Leave immersive mode alone rather than dropping
            // the chrome-hiding class over a surface that is staying put.
            return;
        }
        this.document.body.classList.remove(this.config.ImmersiveBodyClass);
        // GetAppUrl, not a hand-rolled '/app/' + name: an application may carry a
        // custom Path, and ApplicationManager documents its URL builder as the
        // single source of truth precisely because a hand-made slug diverges from
        // what the route resolver matches on.
        void this.router.navigateByUrl(this.apps.GetAppUrl(target));
    }

    /**
     * The application "Back to Explorer" should land on: Home when the user holds
     * it, otherwise their first other app. Null when this surface's own apps are
     * the only ones they hold.
     */
    private get OtherApplicationTarget(): BaseApplication | null {
        const own = new Set(this.config.OwnApplicationNames);
        const others = this.apps.GetAllApps().filter((app) => !own.has(app.Name));
        return others.find((app) => app.Name === EXPLORER_HOME_APP_NAME) ?? others[0] ?? null;
    }

    /**
     * Show an in-surface location, pushing the current one onto the back stack. Navigating to the
     * place already shown (re-submitting the query on screen) is not a move, so it adds no Back step.
     *
     * The location's `From` (the crumb's origin) is carried as given, one level deep: an origin nested inside the origin is dropped
     * here exactly as the URL drops it (`EncodeLocationParams`), so the in-memory place and the `f=` a reload decodes are the same.
     */
    NavigateTo(location: AGUAppLocation): void {
        const next = WithOneLevelFrom(location);
        if (IsSameLocation(next, this.location.value)) return;
        this.history.push(this.location.value);
        if (this.history.length > AGUAppSurfaceHostAdapter.MaxHistoryDepth) {
            this.history.shift();
        }
        this.location.next(next);
    }

    /**
     * Replace the current location without a history entry. Used for the initial
     * landing and for entitlement corrections — neither is a place the user
     * navigated to, so neither should be a place Back returns them to.
     */
    ResetTo(location: AGUAppLocation): void {
        this.location.next(WithOneLevelFrom(location));
    }

    /** Return to the previous location, or stay put when there is none. */
    Back(): void {
        const previous = this.history.pop();
        if (previous && previous.ModuleKey.length > 0) {
            this.location.next(previous);
        }
    }

    async SignOut(): Promise<void> {
        this.document.body.classList.remove(this.config.ImmersiveBodyClass);
        try {
            await this.auth.logout();
        } catch {
            // logout() clears client caches before the provider redirect; if the
            // provider step throws we have still torn down local state — nothing
            // useful to surface to the user here.
        }
    }
}
